/** @jsx jsx */
import { jsx } from 'theme-ui';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Box } from '@chakra-ui/core';
import isNil from 'lodash/isNil';
import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';

import uniq from 'lodash/uniq';
import { Redirect } from 'react-router-dom';
import useAccounts from '../../../app/hooks/useAccounts';
import useConfiguration, { IsUnderMaintenanceEnum } from '../../../app/hooks/useConfiguration';
import { useCurrentUserProfile } from '../../../app/hooks/useCurrentUserProfile';
import { MaintenanceView } from '../../../error/MaintenanceView';
import { useStoreState } from '../../../models/hooks';
import { PATH_MAIN } from '../../../routes/constants';
import { CalendarPanel } from '../../calendar/CalendarPanel';
import { CalendarSidebarTabTypeEnum, RecruiterCalendarSidebarTabTypeEnum } from '../../calendar/CalendarSidebarTab';
import { BusinessPositionT } from '../../calendar/types';
import useCalendar from '../../calendar/useCalendar';
import useCalendarEvent from '../../calendar/useCalendarEvent';
import useRecruiter from '../../messaging/seeker/candidate/notes/useRecruiter';
import { NotAuthorizedView } from '../NotAuthorizedView';

export const RecruiterCalendarView = (): JSX.Element => {
  const appUserId = useStoreState((s) => s.app.user?.id);
  const appOwnersId = useStoreState((s) => s.app.accounts?.owners);

  const isAuthorizationStateLoaded = Boolean(appOwnersId && appUserId);
  const isAdministrationAccount = !isNil(appOwnersId) && !isNil(appUserId) && appOwnersId?.includes(appUserId);

  const { currentUserProfile } = useCurrentUserProfile();
  const [showCalendar, setShowCalendar] = React.useState<boolean>(true);
  const [selectedRecruiters, setSelectedRecruiters] = useState<string[]>([]);
  const [selectedPositions, setSelectedPositions] = useState<BusinessPositionT[]>([]);
  const [selectedRecruitersOfPositions, setSelectedRecruitersOfPositions] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<CalendarSidebarTabTypeEnum>(CalendarSidebarTabTypeEnum.RECRUITERS);
  const [activeCurrentDateRange, setActiveCurrentDateRange] = useState<{ fromDate: Date; toDate: Date }>({
    fromDate: moment(moment().format('YYYY-MM-DDT00:00:00')).toDate(),
    toDate: moment(moment().add(6, 'days').format('YYYY-MM-DDT23:59:59')).toDate(),
  });

  const allRecruiters = useRecruiter();
  const { isCandidatesLoading, isAvailabilityLoading } = useCalendar(
    allRecruiters,
    activeTab === CalendarSidebarTabTypeEnum.RECRUITERS ? selectedRecruiters : selectedRecruitersOfPositions,
    activeCurrentDateRange,
    true,
  );
  useCalendarEvent();

  // istanbul ignore next
  const recruiters = allRecruiters?.filter((r) => r?.anonymousUser !== true && r?.firstName !== 'anonymous');

  // const positions = usePositions();
  const accounts = useAccounts();
  const useConfig = useConfiguration();

  useEffect(() => {
    if (accounts) {
      setShowCalendar(accounts.calendarEnabled === true);
    }
  }, [accounts]);

  useEffect(() => {
    if (currentUserProfile) {
      setSelectedRecruiters([...selectedRecruiters, currentUserProfile.id]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredPositions = (positions: BusinessPositionT[], tab: CalendarSidebarTabTypeEnum) => {
    if (currentUserProfile && tab === CalendarSidebarTabTypeEnum.FOLLOWED_POSITIONS) {
      return positions.filter(
        /* istanbul ignore next */ (position) => position.assignedRecruiters?.some((r) => r.id === currentUserProfile.id),
      );
    }
    return positions;
  };

  const handleAssignedRecruiter = (positions: BusinessPositionT[], tab: CalendarSidebarTabTypeEnum) => {
    let assignedRecruiters: string[] = [];
    filteredPositions(positions, tab).forEach((position) => {
      const positionRecruiters = position.assignedRecruiters?.map(/* istanbul ignore next */ (r) => r.id);
      /* istanbul ignore next */
      if (positionRecruiters && !assignedRecruiters.some((r) => positionRecruiters.includes(r))) {
        assignedRecruiters = assignedRecruiters.concat(positionRecruiters);
      }
    });
    setSelectedRecruitersOfPositions(assignedRecruiters);
  };

  const handleCheckedPositions = (positions: BusinessPositionT[]) => {
    setSelectedPositions(positions);
    handleAssignedRecruiter(positions, activeTab);
  };

  const handleActiveTab = (tab: CalendarSidebarTabTypeEnum | RecruiterCalendarSidebarTabTypeEnum) => {
    setActiveTab(tab as CalendarSidebarTabTypeEnum);
    handleAssignedRecruiter(selectedPositions, tab as CalendarSidebarTabTypeEnum);
  };

  const setActiveDateRange = React.useCallback((fromDate: Date, toDate: Date) => {
    setActiveCurrentDateRange((prev) => {
      // Only create new object if dates actually changed
      /* istanbul ignore next */
      if (
        prev.fromDate.getTime() === fromDate.getTime() &&
        /* istanbul ignore next */ prev.toDate.getTime() === toDate.getTime()
      ) {
        return prev; // Same reference = no re-render
      }
      return { fromDate, toDate };
    });
  }, []);

  if (!showCalendar) {
    return <Redirect to={PATH_MAIN} />;
  }

  if (isAuthorizationStateLoaded && !isAdministrationAccount) {
    return (
      <Box data-testid="NotAuthorizedView">
        <NotAuthorizedView isAuthorizationStateLoaded />
      </Box>
    );
  }

  return (
    <React.Fragment>
      {useConfig === IsUnderMaintenanceEnum.MAINTENANCE && <MaintenanceView />}
      <CalendarPanel
        todayDate={moment().toDate()}
        recruiters={recruiters}
        administrationCalendar
        handleCheckedRecruiters={(rec) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          setSelectedRecruiters([...rec]);
        }}
        checkedRecruiters={selectedRecruiters}
        handleCheckedPositions={handleCheckedPositions}
        checkedPositions={uniq(selectedPositions.map((p) => p.id))}
        activeTab={activeTab}
        setActiveTab={handleActiveTab}
        activeCurrentDateRange={activeCurrentDateRange}
        setActiveDateRange={setActiveDateRange}
        isLoading={isCandidatesLoading}
        isAvailabilityLoading={isAvailabilityLoading}
      />
    </React.Fragment>
  );
};
